import { useState, useEffect, useCallback, useMemo, ChangeEvent } from 'react';
import {
    Box,
    Button,
    Table,
    TableContainer,
    TablePagination,
    Paper,
    TableHead,
    TableRow,
    TableCell,
    Checkbox,
    TableBody,
    CircularProgress,
    Typography,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from 'react-toastify';
import { useLanguage } from '../../../../context/LanguageContext';
import { useUser } from '../../../../context/UserContext';
import useGetCustomersList from '../../../../hooks/Clients/importList/useGetCustomersList';
import useDeleteCustomersList from '../../../../hooks/Clients/importList/useDeleteCustomersList';
import useGetDetailsCustomersList from '../../../../hooks/Clients/importList/useGetDetailsCustomersList';
import DrawerVisualise from '../../ListImport/components/DrawerVisualise';
import ConfirmDialog from '../../ListImport/components/ConfirmDialog';

interface ImportedList {
    LIST_ID: string | number;
    LABEL: string;
    DESCRIPTION?: string;
    IMPORT_DATE?: string;
    TOTAL_ACCOUNTS?: number;
    IS_EMAIL_LIST?: "0" | "1";
    IS_CELL_PHONE_LIST?: "0" | "1";
    IS_WALLET_LIST?: "0" | "1";
    IS_IDENTIFIER_LIST?: "0" | "1";
}

const LIST_TYPES = {
    PERSONAL: "Personal",
    CARD: "Carte",
    EMAIL: "Email",
    MOBILE: "Téléphone mobile",
    WALLET: "Wallet",
};

const TYPE_COLORS: Record<string, "primary" | "info" | "success" | "warning" | "secondary"> = {
    [LIST_TYPES.PERSONAL]: "primary",
    [LIST_TYPES.CARD]: "info",
    [LIST_TYPES.EMAIL]: "success",
    [LIST_TYPES.MOBILE]: "warning",
    [LIST_TYPES.WALLET]: "secondary",
};

const TYPE_TRANSLATION_KEYS: Record<string, string> = {
    [LIST_TYPES.PERSONAL]: "person",
    [LIST_TYPES.CARD]: "card",
    [LIST_TYPES.EMAIL]: "email",
    [LIST_TYPES.MOBILE]: "mobilephone",
    [LIST_TYPES.WALLET]: "wallet",
};

const getListType = (list: ImportedList): string => {
    if (list.IS_EMAIL_LIST === "1") return LIST_TYPES.EMAIL;
    if (list.IS_CELL_PHONE_LIST === "1") return LIST_TYPES.MOBILE;
    if (list.IS_WALLET_LIST === "1") return LIST_TYPES.WALLET;
    if (list.IS_IDENTIFIER_LIST === "1") return LIST_TYPES.CARD;
    return LIST_TYPES.PERSONAL;
};

const convertToDeleteFormat = (list: ImportedList) => {
    const isEmailList = list.IS_EMAIL_LIST === "1";
    const isCellPhoneList = list.IS_CELL_PHONE_LIST === "1";
    const isWalletList = list.IS_WALLET_LIST === "1";
    const isCardList = list.IS_IDENTIFIER_LIST === "1";
    const isPersonIdList = !isEmailList && !isCellPhoneList && !isWalletList && !isCardList;
    
    return {
        list_id: list.LIST_ID,
        is_email_list: isEmailList ? "1" : "0",
        is_cell_phone_list: isCellPhoneList ? "1" : "0",
        is_wallet_list: isWalletList ? "1" : "0",
        is_card_list: isCardList ? "1" : "0",
        is_personid_list: isPersonIdList ? "1" : "0",
    };
};

function MyListsTab() {
    const { t } = useLanguage();
    const { loadingUserInfos } = useUser();
    const { getCustomersList, loading: loadingLists, data: listsData, invalidateListsCache } = useGetCustomersList();
    const { getDetailsCustomersList, loading: loadingDetails, data: detailsData } = useGetDetailsCustomersList();
    const { deleteCustomersList, loading: loadingDelete } = useDeleteCustomersList();
    
    const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerData, setDrawerData] = useState<ImportedList | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
    });

    const importedLists = listsData?.member || [];
    const totalItems = listsData?.totalItems || 0;

    const tableMinHeight = useMemo(() => {
        const rowHeight = 68;
        const headerHeight = 56;
        const paddingHeight = 10;
        if (loadingLists) return headerHeight + (rowsPerPage * rowHeight) + paddingHeight;
        const displayedRows = Math.max(1, importedLists.length);
        return headerHeight + (displayedRows * rowHeight) + paddingHeight;
    }, [rowsPerPage, importedLists.length, loadingLists]);

    useEffect(() => {
        getCustomersList({ page: page + 1, itemsPerPage: rowsPerPage });
    }, [page, rowsPerPage, getCustomersList]);

    const handleVisualise = useCallback(async (row: ImportedList) => {
        setDrawerData(row);
        setDrawerOpen(true);
        await getDetailsCustomersList({ list_id: row.LIST_ID });
    }, [getDetailsCustomersList]);

    const handleRowSelect = useCallback((id: string | number) => {
        setSelectedRows(prev => 
            prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedRows(prev => 
            prev.length > 0 ? [] : importedLists.map(row => row.LIST_ID)
        );
    }, [importedLists]);

    const handleChangePage = useCallback((_event: unknown, newPage: number) => {
        setPage(newPage);
        setSelectedRows([]);
    }, []);

    const handleChangeRowsPerPage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        setSelectedRows([]);
    }, []);

    const handleDelete = useCallback(async (list: ImportedList) => {
        setConfirmDialog({
            open: true,
            title: t("common.delete"),
            message: t("common.confirmDelete"),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                try {
                    const result = await deleteCustomersList([convertToDeleteFormat(list)]);
                    if (result) {
                        toast.success(t("common.deleteSuccess"));
                        invalidateListsCache();
                        if (importedLists.length === 1 && page > 0) {
                            setPage(page - 1);
                        } else {
                            getCustomersList({ page: page + 1, itemsPerPage: rowsPerPage });
                        }
                    }
                } catch (err) {
                    toast.error(t("common.error"));
                }
            },
        });
    }, [deleteCustomersList, t, getCustomersList, page, rowsPerPage, importedLists.length]);

    const handleDeleteBatch = useCallback(async () => {
        if (selectedRows.length === 0) return;
        setConfirmDialog({
            open: true,
            title: t("common.delete"),
            message: t("common.confirmDeleteBatch").replace("{count}", selectedRows.length.toString()),
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                try {
                    const listsToDelete = importedLists
                        .filter(list => selectedRows.includes(list.LIST_ID))
                        .map(convertToDeleteFormat);
                    const result = await deleteCustomersList(listsToDelete);
                    if (result) {
                        toast.success(t("common.deleteBatchSuccess").replace("{count}", selectedRows.length.toString()));
                        invalidateListsCache();
                        setSelectedRows([]);
                        if (selectedRows.length === importedLists.length && page > 0) {
                            setPage(page - 1);
                        } else {
                            getCustomersList({ page: page + 1, itemsPerPage: rowsPerPage });
                        }
                    }
                } catch (err) {
                    toast.error(t("common.error"));
                }
            },
        });
    }, [selectedRows, importedLists, deleteCustomersList, t, getCustomersList, page, rowsPerPage]);

    return (
        <Box>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', minHeight: `${tableMinHeight}px` }}>
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                        <col style={{ width: '48px' }} />
                        <col style={{ width: '35%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '10%' }} />
                    </colgroup>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={selectedRows.length === importedLists.length && importedLists.length > 0}
                                    indeterminate={selectedRows.length > 0 && selectedRows.length < importedLists.length}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Liste</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Date d'import</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">Nb comptes</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">Type de liste</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loadingLists || loadingUserInfos ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : importedLists.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                    <Typography color="text.secondary">Aucune liste trouvée</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            importedLists.map(list => {
                                const listType = getListType(list);
                                return (
                                    <TableRow key={list.LIST_ID} hover>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedRows.includes(list.LIST_ID)}
                                                onChange={() => handleRowSelect(list.LIST_ID)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={list.LABEL} arrow>
                                                <span style={{
                                                    maxWidth: 800,
                                                    display: "inline-block",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    verticalAlign: "bottom"
                                                }}>
                                                    {list.LABEL}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {list.IMPORT_DATE ? new Date(list.IMPORT_DATE).toLocaleString('fr-FR') : "-"}
                                        </TableCell>
                                        <TableCell align="center">{list.TOTAL_ACCOUNTS || 0}</TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                variant="outlined"
                                                color={TYPE_COLORS[listType] || "primary"}
                                                size="small"
                                                label={t(`pages.clients.importList.${TYPE_TRANSLATION_KEYS[listType] || 'person'}`)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" gap={1} justifyContent="center">
                                                <Tooltip title={t("common.details")}>
                                                    <IconButton size="small" color="primary" onClick={() => handleVisualise(list)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t("common.delete")}>
                                                    <IconButton size="small" color="error" onClick={() => handleDelete(list)}>
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 2 }}>
                <Button
                    variant="contained"
                    color="error"
                    disabled={selectedRows.length === 0 || loadingDelete}
                    onClick={handleDeleteBatch}
                >
                    {loadingDelete ? t("common.deleting") : t("common.delete")} {selectedRows.length > 0 && `(${selectedRows.length})`}
                </Button>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalItems}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("common.rowsPerPage")}
                    sx={{ marginLeft: 'auto' }}
                />
            </Box>

            <DrawerVisualise
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                data={drawerData}
                detailsData={detailsData}
                loadingDetails={loadingDetails}
            />
            
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
                confirmText={t("common.confirm")}
                cancelText={t("common.cancel")}
                confirmColor="error"
            />
        </Box>
    );
};

export default MyListsTab;
